/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { useSelector } from 'react-redux'
import { getCustomerProfile } from 'src/selectors/customer'
import {
  CustomerCollectedDataStatus,
  CustomerCollectedDataType,
  CustomerResubmitInfoType,
} from 'src/constants/customer/enums'
import { ROUTES } from 'src/constants/routes'
import { MCStepItem, MCStepParams } from '../mc-details/shared/MCStepItem'
import { MCStepHeader } from '../mc-details/shared/MCStepHeader'
import { MCStepDetails } from '../mc-details/types'
import { DataRow } from '../shared/DataRow'
import { MCStepDocuments } from '../mc-details/shared/MCStepDocuments'
import { Button } from '../shared/Button'
import { CircleCancelMinor } from '@shopify/polaris-icons'
import { getMCBusinessType } from 'src/utils/mcUtils'

const CustomerComplianceComponent = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const customerProfile = useSelector(getCustomerProfile)

  const container = useMemo(() => document.getElementById('root'), [])

  const [stepDetails, setStepDetails] = useState<MCStepDetails | null>(null)

  const handleClose = useCallback(() => {
    setStepDetails(null)
    navigate(-1)
  }, [navigate])

  const steps: MCStepParams[] = useMemo(() => {
    if (!customerProfile) {
      return []
    }

    const res: MCStepParams[] = [
      {
        stepStatus: CustomerCollectedDataStatus.Completed,
        stepType: CustomerResubmitInfoType.BusinessInfo,
        onResubmitInfo: () => {
          navigate(`${ROUTES.CUSTOMER_RESUBMIT_INFO_URL}/${CustomerResubmitInfoType.BusinessInfo}`)
        },
        onViewDetails: !!customerProfile.partnerInformation
          ? () => {
              const data = customerProfile.partnerInformation!

              const businessData = data.businessType ? getMCBusinessType(data.businessType) : null

              const businessType = businessData?.businessType || '-'
              const otherBusinessType = businessData?.otherBusinessType || '-'

              setStepDetails({
                stepData: [
                  {
                    title: 'Business type',
                    text: businessType || '-',
                  },
                  ...(otherBusinessType
                    ? [
                        {
                          title: 'Other business type',
                          text: otherBusinessType,
                        },
                      ]
                    : []),
                  {
                    title: 'DOT number',
                    text: data.dotNumber || '-',
                  },
                  {
                    title: 'Address - 1',
                    text: data.addressStreet1 || '-',
                  },
                  {
                    title: 'Address - 2',
                    text: data.addressStreet2 || '-',
                  },
                  {
                    title: 'State',
                    text: data.addressState || '-',
                  },
                  {
                    title: 'Zip code',
                    text: data.addressPostalCode || '-',
                  },
                ],
                stepStatus: CustomerCollectedDataStatus.Completed,
                stepType: CustomerResubmitInfoType.BusinessInfo,
                documents: [],
              })
            }
          : undefined,
      },
    ]

    customerProfile.collectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.TSA) {
        const stepData = [
          {
            title:
              'Registered Agent and a Certified IAC (Indirect Air Carrier) established in VeRoot with TSA certified drivers',
            text: data.data?.isRegisteredAgent ? 'YES' : 'NO',
          },
        ]

        if (data.data?.isRegisteredAgent) {
          stepData.push(
            {
              title: 'IAC Number',
              text: data.data?.iacNumber || '-',
            },
            {
              title: 'Date of Last Certification',
              text: data.data?.dateOfLastCertification || '-',
            },
            {
              title: 'Agent Name',
              text: data.data?.agentName || '-',
            },
            {
              title: 'Coordinator Name',
              text: data.data?.coordinatorName || '-',
            },
            {
              title: 'Coordinator Phone Number',
              text: data.data?.coordinatorPhoneNumber || '-',
            },
            {
              title: 'Coordinator Email',
              text: data.data?.coordinatorEmail || '-',
            },
          )
        } else {
          stepData.push(
            {
              title: 'Has TWIC',
              text: data.data?.hasTwic ? 'YES' : 'NO',
            },
            {
              title: 'TWIC Contractors Number',
              text: `${data.data?.twicContractorsNumber}` || '-',
            },
            {
              title: 'Has FAST',
              text: data.data?.hasFast ? 'YES' : 'NO',
            },
            {
              title: 'FAST Contractors Number',
              text: `${data.data?.fastContractorsNumber}` || '-',
            },
            {
              title: 'Has SIDA',
              text: data.data?.hasSida ? 'YES' : 'NO',
            },
            {
              title: 'SIDA Contractors Number',
              text: `${data.data?.sidaContractorsNumber}` || '-',
            },
            {
              title: 'Has Hazmat',
              text: data.data?.hasHazmat ? 'YES' : 'NO',
            },
            {
              title: 'Hazmat Contractors Number',
              text: `${data.data?.hazmatContractorsNumber}` || '-',
            },
          )
        }

        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          daysToExpire: data.daysToExpire,
          onResubmitInfo: () => {
            navigate(`${ROUTES.CUSTOMER_RESUBMIT_INFO_URL}/${CustomerCollectedDataType.TSA}`)
          },
          onViewDetails: !!customerProfile.partnerInformation
            ? () => {
                setStepDetails({
                  stepData: stepData,
                  stepStatus: data.status,
                  stepType: CustomerCollectedDataType.TSA,
                  documents: data.documents,
                  expiresAt: data.expiresAt || undefined,
                  daysToExpire: data.daysToExpire,
                })
              }
            : undefined,
        })
      }
    })

    customerProfile.collectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.Insurance) {
        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          daysToExpire: data.daysToExpire,
          onResubmitInfo: () => {
            navigate(
              `${ROUTES.CUSTOMER_RESUBMIT_INFO_URL}/${CustomerCollectedDataType.Insurance}/${data.id}`,
            )
          },
          onViewDetails: data.documents?.length
            ? () => {
                setStepDetails({
                  stepData: [],
                  stepStatus: data.status,
                  stepType: CustomerCollectedDataType.Insurance,
                  documents: data.documents,
                  expiresAt: data.expiresAt || undefined,
                  daysToExpire: data.daysToExpire,
                })
              }
            : undefined,
        })
      }
    })

    customerProfile.collectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.BusinessDetails) {
        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          daysToExpire: data.daysToExpire,
          onResubmitInfo: () => {
            navigate(
              `${ROUTES.CUSTOMER_RESUBMIT_INFO_URL}/${CustomerCollectedDataType.BusinessDetails}`,
            )
          },
          onViewDetails: data.documents?.length
            ? () => {
                setStepDetails({
                  stepData: [
                    {
                      title: 'Does Business Advertise',
                      text: data.data?.doesBusinessAdvertise ? 'YES' : 'NO',
                    },
                    {
                      title: 'Has Quality Program',
                      text: data.data?.hasQualityProgram ? 'YES' : 'NO',
                    },
                    {
                      title: 'Covered Cities Details',
                      text: data.data?.coveredCitiesDetails || '-',
                    },
                    {
                      title: 'Vehicle Access Details',
                      text: data.data?.vehicleAccessDetails || '-',
                    },
                    {
                      title: 'Quality Program Details',
                      text: data.data?.qualityProgramDetails || '-',
                    },
                  ],
                  stepStatus: data.status,
                  stepType: CustomerCollectedDataType.BusinessDetails,
                  documents: data.documents,
                  expiresAt: data.expiresAt || undefined,
                  daysToExpire: data.daysToExpire,
                })
              }
            : undefined,
        })
      }
    })

    if (customerProfile.paymentMethod?.isCompleted && customerProfile.paymentMethod) {
      const directPaymentStatus = CustomerCollectedDataStatus.Completed

      res.push({
        stepStatus: directPaymentStatus,
        stepType: CustomerResubmitInfoType.PaymentMethod,
        onResubmitInfo: () => {
          navigate(`${ROUTES.CUSTOMER_RESUBMIT_INFO_URL}/${CustomerResubmitInfoType.PaymentMethod}`)
        },
        onViewDetails: () => {
          const paymentMethod = customerProfile.paymentMethod!
          const stepData = []

          if (paymentMethod.paymentMethodType) {
            stepData.push({
              title: 'Settlement Method Type',
              text: paymentMethod.paymentMethodType || '-',
            })
          }

          if (paymentMethod.bankAccountType) {
            stepData.push({
              title: 'Account Type',
              text: paymentMethod.bankAccountType || '-',
            })
          }

          if (paymentMethod.bankAccountNumberLast4) {
            stepData.push({
              title: 'Account Number (Last 4)',
              text: `** ${paymentMethod.bankAccountNumberLast4}`,
            })
          }

          if (paymentMethod.bankRoutingNumberLast4) {
            stepData.push({
              title: 'Routing Number (Last 4)',
              text: `** ${paymentMethod.bankRoutingNumberLast4}`,
            })
          }

          if (paymentMethod.cardNumberLast4) {
            stepData.push({
              title: 'Card Number (Last 4)',
              text: `** ${paymentMethod.cardNumberLast4}`,
            })
          }

          if (paymentMethod.cardExpiration) {
            stepData.push({
              title: 'Card Expiration',
              text: paymentMethod.cardExpiration || '-',
            })
          }

          if (paymentMethod.recipientType) {
            stepData.push({
              title: 'Recipient Type',
              text: paymentMethod.recipientType || '-',
            })
          }

          if (paymentMethod.status) {
            stepData.push({
              title: 'Status',
              text: paymentMethod.status || '-',
            })
          }

          setStepDetails({
            stepData: stepData,
            stepStatus: directPaymentStatus,
            stepType: CustomerResubmitInfoType.PaymentMethod,
            documents: [],
          })
        },
      })
    }

    customerProfile.collectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.GigsafeContract) {
        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          daysToExpire: data.daysToExpire,
          onResubmitInfo: () => {
            navigate(
              `${ROUTES.CUSTOMER_RESUBMIT_INFO_URL}/${CustomerCollectedDataType.GigsafeContract}`,
            )
          },
          onViewDetails: data.documents?.length
            ? () => {
                setStepDetails({
                  stepData: [],
                  stepStatus: data.status,
                  stepType: CustomerCollectedDataType.GigsafeContract,
                  documents: data.documents,
                  expiresAt: data.expiresAt || undefined,
                  daysToExpire: data.daysToExpire,
                })
              }
            : undefined,
        })
      }
    })

    return res
  }, [customerProfile])

  const closeStepDetails = useCallback(() => {
    setStepDetails(null)
  }, [])

  if (!location.state?.background) {
    return null
  }

  return (
    <Routes>
      <Route
        path="compliance"
        element={
          <Modal
            container={container}
            open
            onClose={handleClose}
            aria-labelledby="parent-modal-title"
            aria-describedby="parent-modal-description"
            css={tw`flex flex-row w-full h-full`}
            data-testid="customer-account-compliance-modal"
          >
            <Fragment>
              <div
                data-testid="customer-account-compliance-sidebar"
                css={tw`w-[458px] h-full bg-white flex flex-col`}
              >
                <ModalHeader
                  title="Compliance"
                  onClose={handleClose}
                  className={tw`text-black h-[48px]`}
                  closeButtonDataTestId="customer-account-compliance-modal-close-btn"
                />
                <div css={tw`flex flex-col h-[calc(100vh-48px)] bg-[#F6F6F6]`}>
                  <div
                    data-testid="customer-account-compliance-steps-list"
                    css={tw`h-full w-full overflow-y-auto p-4`}
                  >
                    <Typography
                      css={tw`border-0 border-b border-solid border-[#DAD9E0] pb-4 mb-4`}
                      color={colors.SHADES_GREY_400}
                      variant="body2"
                    >
                      Here's a list of your submitted documents. To remain compliant, please keep
                      them up to date and upload new versions if anything changes.
                    </Typography>
                    {steps.map(
                      ({
                        stepStatus,
                        stepType,
                        expiresAt,
                        onResubmitInfo,
                        onChangeExpirationDate,
                        onViewDetails,
                      }) => (
                        <MCStepItem
                          stepStatus={stepStatus}
                          key={stepType}
                          stepType={stepType}
                          expiresAt={expiresAt}
                          onResubmitInfo={onResubmitInfo}
                          onViewDetails={onViewDetails}
                          onChangeExpirationDate={onChangeExpirationDate}
                        />
                      ),
                    )}
                  </div>
                </div>
              </div>
              {stepDetails && (
                <div css={tw`flex flex-1 p-6 max-w-[560px] w-full pointer-events-none`}>
                  <div
                    data-private={
                      stepDetails.stepType === CustomerResubmitInfoType.PaymentMethod
                        ? true
                        : undefined
                    }
                    css={tw`rounded-2xl shadow-lg bg-white py-4 min-w-[380px] flex flex-col pointer-events-auto overflow-y-auto h-full w-full`}
                  >
                    <div css={tw`px-4`}>
                      <MCStepHeader
                        stepStatus={stepDetails.stepStatus}
                        stepType={stepDetails.stepType}
                        expiresSoon={
                          stepDetails.daysToExpire != null &&
                          Number(stepDetails.daysToExpire) >= 0 &&
                          Number(stepDetails.daysToExpire) <= 30
                        }
                      />
                      <div css={tw`w-full mt-4 h-[1px] bg-[#EDEDED]`} />
                    </div>
                    <div css={tw`flex-1 overflow-y-auto px-4`}>
                      {stepDetails.stepData.map((item) => (
                        <DataRow key={item.title} title={item.title} text={item.text} />
                      ))}
                      {!!stepDetails.documents?.length && (
                        <MCStepDocuments
                          documents={stepDetails.documents}
                          header={
                            <MCStepHeader
                              stepStatus={stepDetails.stepStatus}
                              stepType={stepDetails.stepType}
                              expiresSoon={
                                stepDetails.daysToExpire != null &&
                                Number(stepDetails.daysToExpire) >= 0 &&
                                Number(stepDetails.daysToExpire) <= 30
                              }
                            />
                          }
                        />
                      )}
                    </div>

                    <div css={tw`flex justify-end mt-4 flex-col gap-4 px-4`}>
                      <Button
                        fullWidth
                        color="white"
                        size="small"
                        onClick={closeStepDetails}
                        endIcon={<CircleCancelMinor width={20} />}
                        data-testid="customer-account-compliance-detail-close-btn"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Fragment>
          </Modal>
        }
      />
    </Routes>
  )
}

export const CustomerCompliance = memo(CustomerComplianceComponent)
